import {ModelResponse, nullify, ResponseData} from '@dvrd/dvr-controls';

import IDate, {DateType} from '@dvrd/idate';

import {ISO_DATE_FORMAT} from '../util/constants';

import {LVBSize} from '../util/enums';

import {
    createProductCostsV2,
    deleteProductCostsV2,
    getProductCostsV2,
    updateProductCostsV2
} from '../util/requestSender';

import {BaseFields, BaseModel, BaseProps, createFieldDef, FieldDefinition, ModelFieldType} from './baseModel';
import {deepClone} from '../util/utils.ts';

interface Props extends BaseProps {
    id?: string;
    account_id?: string;
    cost_price?: number | null;
    ean?: string;
    lvb_size?: LVBSize | null;
    misc_costs?: number | null;
    misc_costs_description?: string | null;
    misc_return_costs?: number | null;
    misc_return_description?: string | null;
    pick_costs?: number | null;
    send_costs?: number | null;
    start_date?: string;
    min_quantity?: number;
    flex?: Record<string, any>;
}

export interface ProductCostsV2Fields extends BaseFields {
    id: string;
    ean: string;
    start_date: string;
    cost_price: number | null;
    lvb_size: LVBSize | null;
    pick_costs: number | null;
    send_costs: number | null;
    misc_return_costs: number | null;
    misc_return_description: string | null;
    misc_costs: number | null;
    misc_costs_description: string | null;
    account_id: string;
    min_quantity: number;
    flex: Record<string, any>;
}

const fields: ProductCostsV2Fields = {
    id: '',
    created_at: '',
    updated_at: '',
    ean: '',
    start_date: '',
    cost_price: 0,
    lvb_size: null,
    pick_costs: 0,
    send_costs: 0,
    misc_return_costs: 0,
    misc_return_description: '',
    misc_costs: 0,
    misc_costs_description: '',
    account_id: '',
    min_quantity: 1,
    flex: {}
};

const fieldsDefinition: {[key in keyof ProductCostsV2Fields]: FieldDefinition} = {
    id: createFieldDef(ModelFieldType.STRING),
    created_at: createFieldDef(ModelFieldType.STRING),
    updated_at: createFieldDef(ModelFieldType.STRING, undefined, true),
    ean: createFieldDef(ModelFieldType.STRING),
    start_date: createFieldDef(ModelFieldType.STRING),
    // Nullable per ProductCostsV2Fields and per the backend (`Decimal | None`).
    // Without allowNull every clone() — one per keystroke — turns an unset
    // amount into 0, which reads as "this product is free" in profit maths.
    cost_price: createFieldDef(ModelFieldType.NUMBER, null, true),
    pick_costs: createFieldDef(ModelFieldType.NUMBER, null, true),
    send_costs: createFieldDef(ModelFieldType.NUMBER, null, true),
    misc_return_costs: createFieldDef(ModelFieldType.NUMBER, null, true),
    misc_return_description: createFieldDef(ModelFieldType.STRING, null, true),
    misc_costs: createFieldDef(ModelFieldType.NUMBER, null, true),
    misc_costs_description: createFieldDef(ModelFieldType.STRING, null, true),
    account_id: createFieldDef(ModelFieldType.STRING),
    lvb_size: createFieldDef(ModelFieldType.STRING, null, true),
    min_quantity: createFieldDef(ModelFieldType.NUMBER, 1),
    flex: createFieldDef(ModelFieldType.OBJECT, {})
};

const ignoreProps: Array<keyof ProductCostsV2Fields> = [];

export default class ProductCostsV2 extends BaseModel<Props, ProductCostsV2Fields> {
    static get = (_id: string, _callback: ModelResponse<ProductCostsV2>) => {};

    static getAll = (
        accountIDs: Array<string>,
        ean: string,
        callback: ModelResponse<Array<ProductCostsV2>>,
        errorCallback?: (response: Response) => void
    ) => {
        return getProductCostsV2({
            data: {ean, account_ids: accountIDs},
            errorCallback,
            callback: (data: ResponseData) => {
                const costs: Array<ProductCostsV2> = data.success
                    ? data.product_costs.map((cost: object) => new ProductCostsV2(cost))
                    : [];
                callback(costs, data.success, data);
            }
        });
    };

    constructor(props?: Props) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build(props);
    }

    get id(): string {
        return this.fields.id;
    }

    get ean(): string {
        return this.fields.ean;
    }

    set ean(ean: string) {
        this.fields.ean = ean;
    }

    get startDate(): string {
        return this.fields.start_date;
    }

    get startDateAsDate(): IDate {
        return new IDate(this.startDate, {dateType: DateType.DATE_ONLY});
    }

    set startDate(start_date: string) {
        this.fields.start_date = start_date;
    }

    get costPrice(): number | null {
        return this.fields.cost_price;
    }

    set costPrice(cost_price: number | null) {
        this.fields.cost_price = cost_price;
    }

    get lvbSize(): LVBSize | null {
        return this.fields.lvb_size;
    }

    set lvbSize(lvb_size: LVBSize | null) {
        this.fields.lvb_size = lvb_size;
    }

    get pickCosts(): number | null {
        return this.fields.pick_costs;
    }

    set pickCosts(pick_costs: number | null) {
        this.fields.pick_costs = pick_costs;
    }

    get sendCosts(): number | null {
        return this.fields.send_costs;
    }

    set sendCosts(send_costs: number | null) {
        this.fields.send_costs = send_costs;
    }

    get miscReturnCosts(): number | null {
        return this.fields.misc_return_costs;
    }

    set miscReturnCosts(misc_return_costs: number | null) {
        this.fields.misc_return_costs = misc_return_costs;
    }

    get miscReturnDescription(): string | null {
        return this.fields.misc_return_description;
    }

    set miscReturnDescription(misc_return_description: string | null) {
        this.fields.misc_return_description = misc_return_description;
    }

    get miscCosts(): number | null {
        return this.fields.misc_costs;
    }

    set miscCosts(misc_costs: number | null) {
        this.fields.misc_costs = misc_costs;
    }

    get miscCostsDescription(): string | null {
        return this.fields.misc_costs_description;
    }

    set miscCostsDescription(misc_costs_description: string | null) {
        this.fields.misc_costs_description = misc_costs_description;
    }

    get accountId(): string {
        return this.fields.account_id;
    }

    set accountId(account_id: string) {
        this.fields.account_id = account_id;
    }

    get minQuantity(): number {
        return this.fields.min_quantity;
    }

    set minQuantity(min_quantity: number) {
        this.fields.min_quantity = min_quantity;
    }

    get flex(): Record<string, any> {
        return this.fields.flex;
    }

    set flex(value: Record<string, any>) {
        this.fields.flex = value;
    }

    get costsTotal(): number {
        return (this.costPrice ?? 0) + (this.pickCosts ?? 0) + (this.sendCosts ?? 0) + (this.miscCosts ?? 0);
    }

    setFlex = (key: string, value: any) => {
        this.fields.flex[key] = value;
        return this;
    };

    getFlex = <T>(key: string): T | undefined => {
        return this.fields.flex[key];
    };

    clone = (updateParams?: Partial<Record<keyof ProductCostsV2Fields, any>>): ProductCostsV2 => {
        return new ProductCostsV2({...deepClone(this.fields), ...updateParams});
    };

    create = (callback: ModelResponse<ProductCostsV2>, errorCallback?: (response: Response) => void) => {
        return createProductCostsV2({
            errorCallback,
            data: {
                ean: this.ean,
                cost_price: this.costPrice,
                misc_costs: this.miscCosts,
                misc_costs_description: nullify(this.miscCostsDescription),
                misc_return_costs: this.miscReturnCosts,
                send_costs: this.sendCosts,
                pick_costs: this.pickCosts,
                min_quantity: this.minQuantity,
                account_id: this.accountId,
                lvb_size: nullify(this.lvbSize),
                start_date: this.startDateAsDate.format(ISO_DATE_FORMAT),
                misc_return_description: nullify(this.miscReturnDescription),
                flex: Object.keys(this.flex).length ? this.flex : null
            },
            callback: (data: ResponseData) => {
                if (data.success) this.build(data.product_costs);
                callback(this, data.success, data);
            }
        });
    };

    update = (callback: ModelResponse<ProductCostsV2>, errorCallback?: (response: Response) => void) => {
        return updateProductCostsV2({
            errorCallback,
            data: {
                ean: this.ean,
                cost_price: this.costPrice,
                misc_costs: this.miscCosts,
                misc_costs_description: nullify(this.miscCostsDescription),
                misc_return_costs: this.miscReturnCosts,
                send_costs: this.sendCosts,
                pick_costs: this.pickCosts,
                min_quantity: this.minQuantity,
                account_id: this.accountId,
                lvb_size: nullify(this.lvbSize),
                start_date: this.startDateAsDate.format(ISO_DATE_FORMAT),
                misc_return_description: nullify(this.miscReturnDescription),
                id: this.id,
                flex: Object.keys(this.flex).length ? this.flex : null
            },
            callback: (data: ResponseData) => {
                if (data.success) this.build(data.product_costs);
                callback(this, data.success, data);
            }
        });
    };

    delete = (callback: ModelResponse<ProductCostsV2>, errorCallback?: (response: Response) => void) => {
        return deleteProductCostsV2({
            id: this.id,
            account_id: this.accountId,
            errorCallback,
            callback: (data: ResponseData) => {
                callback(this, data.success, data);
            }
        });
    };

    protected build = (props?: Props) => {
        this.constructFields(props);
        if (props) {
            // TODO
        }
    };
}
